import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { doc, updateDoc, Timestamp } from 'firebase/firestore';
import { db, auth } from '../../../firebase';
import { useAuth } from '../../../contexts/AuthContext/AuthContext';
import { hasPerm } from '../../../utils/permissions';
import { ensureLessonForOccurrence } from '../../../services/lessonService';
import { setLessonOutcome, OUTCOME, OUTCOME_META } from '../../../services/lessonOutcome';
import { NUDGE } from '../../../services/lessonNudgesModel';
import useLessonNudges from './useLessonNudges';
import '../portalSurface.css';
import './LessonNudges.css';

/**
 * The lesson nudge popup (Anthony, 2026-09-29): "modal window popups that
 * continue to render until it's pressed", and no snooze.
 *
 * So there is NO close control, Escape does nothing and the backdrop is not a
 * button. Every button RECORDS something, and the row leaves because the
 * record now answers it (useLessonNudges listens live), which is also what
 * clears it on the teacher's other devices.
 *
 * Never over a lesson in progress: on the lesson dashboard the timer pill
 * already says "Not started", and a popup over the notes mid-song is the
 * worst possible moment. It reappears the moment the teacher leaves.
 */

const ATTENDANCE_CHOICES = [OUTCOME.ATTENDED, OUTCOME.NO_SHOW, OUTCOME.CANCELLED_FAMILY];

const formatTime = (time24) => {
	if (!time24 || !time24.includes(':')) return '';
	const [h, m] = time24.split(':');
	const hour = parseInt(h, 10);
	return `${hour % 12 || 12}:${m} ${hour >= 12 ? 'PM' : 'AM'}`;
};

const formatDay = (dateStr, todayStr) => {
	if (dateStr === todayStr) return 'Today';
	const [y, m, d] = dateStr.split('-').map(Number);
	return new Date(y, m - 1, d).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
};

const todayKey = () => {
	const n = new Date();
	return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}-${String(n.getDate()).padStart(2, '0')}`;
};

export default function LessonNudges() {
	const { userProfile, userRole } = useAuth();
	const location = useLocation();
	const navigate = useNavigate();
	const myTeacherId = userProfile?.teacherId || null;
	const isStaff = userRole === 'employee' || userRole === 'admin';
	const enabled = !!(isStaff && myTeacherId && hasPerm(userProfile, 'viewSchedule'));
	const nudges = useLessonNudges({ myTeacherId, enabled });
	const [busy, setBusy] = useState(null);
	const [error, setError] = useState(null);
	const dialogRef = useRef(null);

	const inLesson = location.pathname.startsWith('/portal/work/lesson/');
	const open = enabled && !inLesson && nudges.length > 0;

	useEffect(() => {
		if (open && dialogRef.current) dialogRef.current.focus();
	}, [open]);

	if (!open) return null;

	const run = async (key, fn) => {
		if (busy) return;
		setBusy(key);
		setError(null);
		try {
			await fn();
		} catch (e) {
			console.error('Lesson nudge action failed:', e);
			setError({ key, detail: e?.message || String(e) });
		} finally {
			setBusy(null);
		}
	};

	const startNow = (n) => run(`${n.key}:start`, async () => {
		const id = n.row.lessonDocId || await ensureLessonForOccurrence({
			slotId: n.row.slotId, originDate: n.row.originDate, date: n.date,
			startTime: n.row.time, duration: n.row.duration, studentId: n.row.studentId,
			studentName: n.row.studentName, instrument: n.row.instrument,
			location: n.row.location, isMakeup: n.row.isMakeup,
		});
		// The same write the lesson page's timer pill makes (useLessonTimer.start).
		await updateDoc(doc(db, 'lessons', id), { startedAt: Timestamp.now(), status: 'in-progress' });
		navigate(`/portal/work/lesson/${id}`);
	});

	const mark = (n, value) => run(`${n.key}:${value}`, () => setLessonOutcome({
		row: n.row, value, uid: auth.currentUser?.uid, date: n.date,
	}));

	const today = todayKey();

	return (
		<div className="cyv-nudge__backdrop">
			<div
				className="cyv-nudge"
				role="alertdialog"
				aria-modal="true"
				aria-labelledby="cyv-nudge-title"
				tabIndex={-1}
				ref={dialogRef}>
				<h2 id="cyv-nudge-title" className="cyv-nudge__title">
					{nudges.length === 1 ? 'A lesson needs an answer' : `${nudges.length} lessons need an answer`}
				</h2>
				<p className="cyv-nudge__lead">This stays open until each lesson below is answered.</p>
				<ul className="cyv-nudge__list">
					{nudges.map((n) => (
						<li key={n.key} className="cyv-nudge__row">
							<div className="cyv-nudge__who">
								<span className="cyv-nudge__name">{n.studentName}</span>
								<span className="cyv-nudge__when">
									{formatDay(n.date, today)} · {formatTime(n.time)}{n.location ? ` · ${n.location}` : ''}
								</span>
								<span className="cyv-nudge__ask">
									{n.kind === NUDGE.START ? 'Has not been started.' : 'Was the student here?'}
								</span>
							</div>
							<div className="cyv-nudge__actions">
								{n.kind === NUDGE.START ? (
									<>
										<button type="button" className="ps-btn ps-btn--primary cyv-nudge__btn cyv-nudge__btn--main"
											onClick={() => startNow(n)} disabled={!!busy}>
											<i className={`fa-sharp fa-solid ${busy === `${n.key}:start` ? 'fa-spinner fa-spin' : 'fa-play'}`} aria-hidden="true" />
											Start now
										</button>
										<button type="button" className="ps-btn cyv-nudge__btn"
											onClick={() => mark(n, OUTCOME.NO_SHOW)} disabled={!!busy}>
											Student didn&apos;t come
										</button>
									</>
								) : (
									ATTENDANCE_CHOICES.map((value) => (
										<button key={value} type="button"
											className={`ps-btn cyv-nudge__btn${value === OUTCOME.ATTENDED ? ' ps-btn--primary cyv-nudge__btn--main' : ''}`}
											onClick={() => mark(n, value)} disabled={!!busy}>
											<i className={`fa-sharp fa-solid ${busy === `${n.key}:${value}` ? 'fa-spinner fa-spin' : OUTCOME_META[value].icon}`} aria-hidden="true" />
											{OUTCOME_META[value].label}
										</button>
									))
								)}
							</div>
							{error && error.key.startsWith(`${n.key}:`) && (
								<p className="cyv-nudge__error" role="alert">
									That did not save. Please tell Anthony right away.
									<span className="cyv-nudge__error-detail">{error.detail}</span>
								</p>
							)}
						</li>
					))}
				</ul>
			</div>
		</div>
	);
}
